import { Alert, Box, Button, Chip, Stack, Typography, useMediaQuery } from '@mui/material'
import { LockOpenOutlined, LockOutlined, PublishedWithChangesRounded, WarningAmberOutlined } from '@mui/icons-material'
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import LibraryQuestionGroupService from '../../../shared/services/library-question-group/library-question-group.service';
import { ILibraryQuestionGroup } from '../../../shared/services/library-question-group/library-question-group.model';
import { ILibraryPushTarget } from '../../dialogs/partner/partner-library-push-dialog';
import gaiaTheme from '../../../root-components/theme';
import { IHeadCell } from '../../../shared/interfaces/table-display.model';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { IconName } from '../../../shared/interfaces/icon-picker.model';
import { LoadingPage } from '../loading-page';
import { TableDisplay } from '../../presentation-components/miscellaneous-components/table-display';
import { TableActionButtons } from '../../presentation-components/miscellaneous-components/table-action-buttons';
import { PartnerLibraryPushDialog } from '../../dialogs/partner/partner-library-push-dialog';
import { PartnerLibraryDeleteDialog } from '../../dialogs/partner/partner-library-delete-dialog';
import { PartnerLibrarySurveyCreateDialog } from '../../dialogs/partner/partner-library-survey-create-dialog';
import { PartnerLibrarySurveyImportDialog } from '../../dialogs/partner/partner-library-survey-import-dialog';
import { SearchBar } from '../../presentation-components/display-elements-components/search-bar';
import { useDebounce } from '../../../hooks/use-debounce-hook';

// Re-pushing over a client with live responses is allowed, but it should never be a surprise.
const responseDetail = (client: ILibraryPushTarget) => {
  const target = client as ILibraryPushTarget & { responseSessionCount?: number, hasCommittedEmissions?: boolean }
  if (!target.responseSessionCount) return null
  return `${target.responseSessionCount} response${target.responseSessionCount === 1 ? '' : 's'} already submitted${target.hasCommittedEmissions ? ', and emissions committed' : ''}`
}

// The partner's reusable surveys. A library item is a question group carrying its questions,
// options, display conditions and every emission rule; pushing it also creates a DRAFT survey
// in each client for them to complete and activate.
export const PartnerLibrarySurveysPage = () => {
  const libraryService = new LibraryQuestionGroupService()
  const userNotificationsContext = useContext(UserNotificationsContext)
  const navigate = useNavigate()

  const [groups, setGroups] = useState<ILibraryQuestionGroup[]>()
  const [pushTarget, setPushTarget] = useState<{ id: string, name: string }>()
  const [isCreating, setIsCreating] = useState<boolean>(false)
  const [isImporting, setIsImporting] = useState<boolean>(false)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string, name: string, pushedClientCount: number }>()

  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 500)

  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(25)
  const [selected, setSelected] = useState<string[]>([]);
  const [change, setChange] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()

  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const requestId = useRef<number>(0)

  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  useEffect(() => { getAll() }, [inputPage, perPage, change])

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) setChange(change + 1)
      else setInputPage(0)
    }
    setHasPageMounted(true)
  }, [order, orderBy])

  useEffect(() => {
    if (inputPage === 0) setChange(c => c + 1)
    else setInputPage(0)
  }, [debouncedSearchQuery])

  const getAll = () => {
    requestId.current++
    const currentRequestId = requestId.current
    libraryService.getAllWithPagination(inputPage, perPage, orderBy, order, debouncedSearchQuery || undefined)
      .then(response => {
        if (currentRequestId === requestId.current) {
          setIsLoading(false)
          setTotalRecords(response.count)
          setGroups(response.rows)
        }
      })
      .catch(err => {
        if (currentRequestId === requestId.current) setIsLoading(false)
        console.log(err)
      })
  }

  const headCells: IHeadCell[] = [
    { id: 'action', numeric: false, label: 'Action', sortable: false, includeInMobile: false },
    { id: 'name', numeric: false, label: 'Name', sortable: true, isHeading: true },
    { id: 'contents', numeric: false, label: 'Contents', sortable: false },
    { id: 'clients', numeric: false, label: 'Clients', sortable: false },
    { id: 'clientEditing', numeric: false, label: 'Client editing', sortable: false },
  ];

  const rows = !groups ? [] : groups.map((group) => ({
    id: group.id,
    name: group.name,
    contents: `${group.questionCount} question${group.questionCount === 1 ? '' : 's'} · ${group.emissionRuleCount} rule${group.emissionRuleCount === 1 ? '' : 's'}`,
    clients: (
      <Stack direction='row' alignItems='center' spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        <Typography variant='body2'>
          {group.pushedClientCount === 0 ? 'Not pushed' : `${group.pushedClientCount} pushed`}
        </Typography>
        {group.outOfDateClientCount > 0 &&
          <Chip size='small' variant='outlined' color='warning' label={`${group.outOfDateClientCount} out of date`} />}
        {group.editedByClientCount > 0 &&
          <Chip size='small' variant='outlined' color='info' label={`${group.editedByClientCount} edited by client`} />}
        {group.noLongerPresentCount > 0 &&
          <Chip size='small' variant='outlined' color='error' label={`${group.noLongerPresentCount} no longer present`} />}
      </Stack>
    ),
    clientEditing: (
      <Chip
        size='small'
        variant='outlined'
        color={group.allowClientEdit ? 'default' : 'success'}
        icon={group.allowClientEdit ? <LockOpenOutlined fontSize='small' /> : <LockOutlined fontSize='small' />}
        label={group.allowClientEdit ? 'Client can edit' : 'Locked'}
      />
    ),
    //Pushing is the page's main per-row action, so it gets an inline button rather
    //than hiding in the kebab (same pattern as the reduction scenarios table).
    action: (
      <Stack direction='row' spacing={0.5} alignItems='center'>
        <Button
          size='small'
          startIcon={<PublishedWithChangesRounded />}
          onClick={() => setPushTarget({ id: group.id, name: group.name })}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
        >
          Push to clients
        </Button>
        <TableActionButtons
          id={group.id}
          includeEdit={true}
          includeDelete={true}
          onEdit={() => navigate(`/partner-library/surveys/${group.id}`)}
          onDelete={() => setDeleteTarget({ id: group.id, name: group.name, pushedClientCount: group.pushedClientCount })}
          additionalMenuItems={[{
            label: group.allowClientEdit ? 'Lock for clients' : 'Allow clients to edit',
            icon: group.allowClientEdit
              ? <LockOutlined sx={{ cursor: 'pointer', mr: 1 }} />
              : <LockOpenOutlined sx={{ cursor: 'pointer', mr: 1 }} />,
            onClick: () => toggleClientEditHandler(group),
          }]}
        />
      </Stack>
    ),
  }))

  const handleOrderChange = (orderBy: string, order: 'asc' | 'desc') => {
    setOrderBy(orderBy)
    setOrder(order)
  };

  const toggleClientEditHandler = (group: ILibraryQuestionGroup) => {
    const next = !group.allowClientEdit
    const apply = () => {
      libraryService.setClientEditPermission(group.id, next)
        .then(() => {
          userNotificationsContext.openSnackbar({
            open: true,
            message: next ? 'Clients can now edit their copy of this survey' : 'This survey is now locked for clients',
          })
          getAll()
        })
        .catch(err => {
          userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
          console.log(err)
        })
    }

    if (next || group.editedByClientCount === 0) { apply(); return }

    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: `${group.editedByClientCount} client${group.editedByClientCount === 1 ? ' has' : 's have'} already changed their copy of this survey. Locking it stops them changing it further. Their current version stays in place until you push over it.`,
      buttonCopy: 'Lock it',
      cancelButtonCopy: 'Cancel',
      hideCancelButton: false
    })
      .then(response => { if (response) apply() })
      .catch(err => {
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
        console.log(err)
      })
  }

  const anyBroken = (groups || []).some(g => (g.integrityProblems || []).length > 0)

  return (
    <PageOuterContainer>
      <PageHeading
        title='Library'
        includeActionButton={true}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={() => setIsCreating(true)}
        actionButtonText='Add Survey'
        includeSecondaryActionButton={true}
        secondaryActionButtonIcon={IconName.UploadOutlined}
        secondaryActionButtonHandler={() => setIsImporting(true)}
        secondaryActionButtonText='Import from client'
        hasTabs={false}
      />
      <Typography variant='body2' sx={{ mb: 2, color: 'text.secondary' }}>
        Build a survey once here, with its questions, display conditions and emission rules, then
        push it to the clients who need it. Each client gets their own copy plus a draft survey to
        complete and send out.
      </Typography>
      {anyBroken && <Alert severity='warning' icon={<WarningAmberOutlined />} sx={{ mb: 2 }}>
        One or more surveys have rules referring to questions that are no longer in them. Open the
        survey to fix it. It can't be pushed until then.
      </Alert>}
      <Box sx={{ mb: 2 }}>
        <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
      </Box>
      <LoadingPage pageReady={!isLoading}>
        {groups && <TableDisplay
          onOrderChange={handleOrderChange}
          isMobileMode={isMobileMode}
          headCells={headCells}
          rows={rows}
          selected={selected}
          setSelected={setSelected}
          selectByIcon={true}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage} />}
      </LoadingPage>

      {pushTarget &&
        <PartnerLibraryPushDialog
          open={Boolean(pushTarget)}
          libraryRuleId={pushTarget.id}
          libraryRuleName={pushTarget.name}
          resourceLabel='survey'
          identityNoun='name'
          api={{
            getPushTargets: (id) => libraryService.getPushTargets(id),
            push: (id, ids, overwriteIds) => libraryService.push(id, ids, overwriteIds),
          }}
          renderClientDetail={(client) => {
            const detail = responseDetail(client)
            return detail
              ? <Typography variant='caption' sx={{ color: 'warning.main' }}>{detail}</Typography>
              : null
          }}
          closeHandler={() => setPushTarget(undefined)}
          onPushed={getAll} />}

      {deleteTarget &&
        <PartnerLibraryDeleteDialog
          open={Boolean(deleteTarget)}
          libraryRuleId={deleteTarget.id}
          libraryRuleName={deleteTarget.name}
          pushedClientCount={deleteTarget.pushedClientCount}
          resourceLabel='survey'
          inUseExplanation="A client who has already sent this survey out, or collected responses against it, keeps their copy, because removing it would destroy their answers."
          deleteResource={(id, removeFromClients) => libraryService.delete(id, removeFromClients)}
          closeHandler={() => setDeleteTarget(undefined)}
          onDeleted={getAll} />}

      <PartnerLibrarySurveyCreateDialog
        open={isCreating}
        closeHandler={() => setIsCreating(false)}
        onCreated={id => navigate(`/partner-library/surveys/${id}`)} />

      <PartnerLibrarySurveyImportDialog
        open={isImporting}
        closeHandler={() => setIsImporting(false)}
        onImported={id => navigate(`/partner-library/surveys/${id}`)} />
    </PageOuterContainer>
  )
}
